import Component from '@glimmer/component';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import Card from 'tio-reimagined/components/card';
import { eq, gt } from 'tio-ui/utilities';
import {
  PhTrendUp,
  PhTrendDown,
  PhChartBar,
  PhGraduationCap,
  PhUsers,
  PhTarget,
  PhArrowUp,
  PhArrowDown,
  PhLightbulb,
} from 'ember-phosphor-icons';

// Mock program effectiveness data
const PROGRAM_DATA = {
  summary: {
    totalPrograms: 156,
    activeEnrollments: 892,
    completionRate: 78,
    avgROI: 185,
    totalSpend: 1845000,
    promotionsFromTA: 67,
  },
  topPerformers: [
    {
      name: 'RN to BSN Bridge Program',
      provider: 'University Partners Network',
      enrolled: 156,
      completed: 134,
      avgTime: '18 months',
      promotionRate: 28,
      roi: 245,
      trend: 'up',
    },
    {
      name: 'Epic EHR Certification',
      provider: 'Epic Systems',
      enrolled: 89,
      completed: 82,
      avgTime: '3 months',
      promotionRate: 31,
      roi: 312,
      trend: 'up',
    },
    {
      name: 'Leadership Essentials Certificate',
      provider: 'Healthcare Leadership Institute',
      enrolled: 67,
      completed: 58,
      avgTime: '6 months',
      promotionRate: 24,
      roi: 198,
      trend: 'stable',
    },
    {
      name: 'Clinical Data Analytics',
      provider: 'Health Informatics Academy',
      enrolled: 34,
      completed: 28,
      avgTime: '12 months',
      promotionRate: 18,
      roi: 156,
      trend: 'up',
    },
  ],
  underperformers: [
    {
      name: 'General Business Admin',
      enrolled: 45,
      completed: 12,
      completionRate: 27,
      roi: 45,
      issue: 'Low completion',
    },
    {
      name: 'Basic Computer Skills',
      enrolled: 78,
      completed: 65,
      completionRate: 83,
      roi: 32,
      issue: 'Low ROI',
    },
    {
      name: 'Project Management Basics',
      enrolled: 34,
      completed: 8,
      completionRate: 24,
      roi: 67,
      issue: 'Low completion',
    },
  ],
  categoryROI: [
    { category: 'Clinical Certifications', spend: 425000, enrolled: 234, roi: 278, trend: 'up' },
    { category: 'Degree Programs', spend: 680000, enrolled: 156, roi: 165, trend: 'stable' },
    { category: 'Leadership Development', spend: 312000, enrolled: 189, roi: 198, trend: 'up' },
    { category: 'Technical Skills', spend: 245000, enrolled: 178, roi: 142, trend: 'down' },
    {
      category: 'Professional Certifications',
      spend: 183000,
      enrolled: 135,
      roi: 156,
      trend: 'stable',
    },
  ],
};

export default class CareerPathwaysAdminProgramEffectiveness extends Component {
  get data() {
    return PROGRAM_DATA;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0 pb-12">
      {{! Header }}
      <div class="flex justify-between items-center mb-6">
        <div>
          <Heading @level={{1}} class="text-2xl font-bold text-gray-900">
            Program Effectiveness
          </Heading>
          <Text @variant="muted">ROI analysis and program performance tracking</Text>
        </div>
        <div class="flex gap-3">
          <Button @appearance="outlined" @intent="primary">
            <PhChartBar @size={{18}} class="mr-2" />
            Export Report
          </Button>
          <Button @intent="primary">
            <PhTarget @size={{18}} class="mr-2" />
            Configure Programs
          </Button>
        </div>
      </div>

      {{! Summary Cards }}
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div class="bg-white rounded-lg p-5 border border-gray-200">
          <Text @variant="muted" @size="sm">Total Programs</Text>
          <div
            class="text-2xl font-bold text-gray-900 mt-1"
          >{{this.data.summary.totalPrograms}}</div>
        </div>
        <div class="bg-white rounded-lg p-5 border border-gray-200">
          <Text @variant="muted" @size="sm">Active Enrollments</Text>
          <div
            class="text-2xl font-bold text-violet-600 dark:text-violet-400 mt-1"
          >{{this.data.summary.activeEnrollments}}</div>
        </div>
        <div class="bg-white rounded-lg p-5 border border-gray-200">
          <Text @variant="muted" @size="sm">Completion Rate</Text>
          <div
            class="text-2xl font-bold text-green-600 dark:text-green-400 mt-1"
          >{{this.data.summary.completionRate}}%</div>
        </div>
        <div class="bg-white rounded-lg p-5 border border-gray-200">
          <Text @variant="muted" @size="sm">Average ROI</Text>
          <div
            class="text-2xl font-bold text-teal-600 dark:text-teal-400 mt-1"
          >{{this.data.summary.avgROI}}%</div>
        </div>
        <div class="bg-white rounded-lg p-5 border border-gray-200">
          <Text @variant="muted" @size="sm">Total TA Spend</Text>
          <div class="text-2xl font-bold text-gray-900 mt-1">$1.85M</div>
        </div>
        <div class="bg-white rounded-lg p-5 border border-gray-200">
          <Text @variant="muted" @size="sm">Promotions (TA)</Text>
          <div
            class="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1"
          >{{this.data.summary.promotionsFromTA}}</div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {{! Top Performers }}
        <div class="lg:col-span-2">
          <Card @padding="none">
            <div class="flex items-center gap-2 p-5 border-b border-gray-100">
              <PhTrendUp @size={{20}} class="text-green-600" />
              <span class="font-medium text-gray-900">Top Performing Programs</span>
            </div>
            <div class="p-5">
              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead>
                    <tr class="text-left text-sm text-gray-500 border-b border-gray-200">
                      <th class="pb-3 font-medium">Program</th>
                      <th class="pb-3 font-medium text-center">Enrolled</th>
                      <th class="pb-3 font-medium text-center">Completed</th>
                      <th class="pb-3 font-medium text-center">Promotion Rate</th>
                      <th class="pb-3 font-medium text-right">ROI</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    {{#each this.data.topPerformers as |program|}}
                      <tr>
                        <td class="py-4">
                          <Text class="font-medium text-gray-900">{{program.name}}</Text>
                          <Text @variant="muted" @size="sm">{{program.provider}}</Text>
                        </td>
                        <td class="py-4 text-center text-gray-600">{{program.enrolled}}</td>
                        <td class="py-4 text-center text-gray-600">{{program.completed}}</td>
                        <td class="py-4 text-center">
                          <Chip @intent="success" @size="sm">{{program.promotionRate}}%</Chip>
                        </td>
                        <td class="py-4 text-right">
                          <div class="flex items-center justify-end gap-1">
                            <span
                              class="font-semibold text-green-600 dark:text-green-400"
                            >{{program.roi}}%</span>
                            {{#if (eq program.trend "up")}}
                              <PhArrowUp @size={{14}} class="text-green-500" />
                            {{else if (eq program.trend "down")}}
                              <PhArrowDown @size={{14}} class="text-red-500" />
                            {{/if}}
                          </div>
                        </td>
                      </tr>
                    {{/each}}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </div>

        {{! Underperformers }}
        <Card @padding="none">
          <div class="flex items-center gap-2 p-5 border-b border-gray-100">
            <PhTrendDown @size={{20}} class="text-red-500" />
            <span class="font-medium text-gray-900">Needs Attention</span>
          </div>
          <div class="p-5">
            <div class="space-y-4">
              {{#each this.data.underperformers as |program|}}
                <div
                  class="p-4 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-100 dark:border-red-800"
                >
                  <div class="flex items-center justify-between mb-2">
                    <Text class="font-semibold text-gray-900">{{program.name}}</Text>
                    <Chip @intent="danger" @size="sm">{{program.issue}}</Chip>
                  </div>
                  <div class="flex items-center gap-4 text-sm">
                    <span class="text-gray-600">Completion:
                      {{program.completionRate}}%</span>
                    <span class="text-gray-600">ROI: {{program.roi}}%</span>
                  </div>
                </div>
              {{/each}}
            </div>
          </div>
        </Card>
      </div>

      {{! Category ROI }}
      <Card @padding="none" class="mt-6">
        <div class="flex items-center gap-2 p-5 border-b border-gray-100">
          <PhGraduationCap @size={{20}} class="text-violet-600" />
          <span class="font-medium text-gray-900">ROI by Program Category</span>
        </div>
        <div class="p-5">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="text-left text-sm text-gray-500 border-b border-gray-200">
                  <th class="pb-3 font-medium">Category</th>
                  <th class="pb-3 font-medium text-center">Total Spend</th>
                  <th class="pb-3 font-medium text-center">Enrollments</th>
                  <th class="pb-3 font-medium text-center">ROI</th>
                  <th class="pb-3 font-medium text-center">Trend</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                {{#each this.data.categoryROI as |cat|}}
                  <tr>
                    <td class="py-4">
                      <Text class="font-medium text-gray-900">{{cat.category}}</Text>
                    </td>
                    <td class="py-4 text-center text-gray-600">
                      ${{cat.spend}}
                    </td>
                    <td class="py-4 text-center text-gray-600">{{cat.enrolled}}</td>
                    <td class="py-4 text-center">
                      {{#if (gt cat.roi 200)}}
                        <Chip @intent="success" @size="sm">{{cat.roi}}%</Chip>
                      {{else if (gt cat.roi 150)}}
                        <Chip @intent="warning" @size="sm">{{cat.roi}}%</Chip>
                      {{else}}
                        <Chip @intent="danger" @size="sm">{{cat.roi}}%</Chip>
                      {{/if}}
                    </td>
                    <td class="py-4 text-center">
                      {{#if (eq cat.trend "up")}}
                        <PhArrowUp @size={{18}} class="text-green-500 mx-auto" />
                      {{else if (eq cat.trend "down")}}
                        <PhArrowDown @size={{18}} class="text-red-500 mx-auto" />
                      {{else}}
                        <span class="text-gray-400">-</span>
                      {{/if}}
                    </td>
                  </tr>
                {{/each}}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

      {{! Insights }}
      <div
        class="mt-6 p-6 bg-violet-50 dark:bg-violet-900/30 rounded-lg border border-violet-200 dark:border-violet-700"
      >
        <Heading
          @level={{3}}
          class="text-lg font-semibold text-violet-900 dark:text-violet-200 mb-4"
        >
          <PhLightbulb @size={{20}} class="inline mr-2" />
          Key Insights
        </Heading>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="flex items-start gap-3">
            <PhUsers @size={{20}} class="text-violet-600 dark:text-violet-400 mt-1 shrink-0" />
            <Text class="dark:text-gray-200">Clinical certifications deliver the highest ROI at
              278%, consider expanding these offerings.</Text>
          </div>
          <div class="flex items-start gap-3">
            <PhTrendDown @size={{20}} class="text-red-500 dark:text-red-400 mt-1 shrink-0" />
            <Text class="dark:text-gray-200">General Business Admin has only 27% completion rate.
              Consider redesigning or replacing.</Text>
          </div>
          <div class="flex items-start gap-3">
            <PhTrendUp @size={{20}} class="text-green-600 dark:text-green-400 mt-1 shrink-0" />
            <Text class="dark:text-gray-200">Epic certification shows 31% promotion rate - highest
              career mobility impact.</Text>
          </div>
          <div class="flex items-start gap-3">
            <PhTarget @size={{20}} class="text-amber-600 dark:text-amber-400 mt-1 shrink-0" />
            <Text class="dark:text-gray-200">Technical skills category ROI declining. Review
              relevance to current role needs.</Text>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}
